import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthMarketplaceTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthAdminService, GrowthAgentListingResponse } from './growth-admin.service';
import { GROWTH_AGENT_INSTALL_LIFECYCLE_STATUSES } from './growth-agent-marketplace-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-marketplace-listings',
  imports: [AinPageComponent, InfoHintComponent, TableModule, FormsModule, ButtonModule, InputTextModule, SelectModule, RouterLink],
  template: `
    <ain-page [title]="t('growth.agentMarketplace.listings.title')" [description]="t('growth.agentMarketplace.listings.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.agentMarketplace.listings.help.title'"
        [descriptionKey]="'growth.agentMarketplace.listings.help.description'"
        [bulletKeys]="['growth.agentMarketplace.listings.help.bullets.curated', 'growth.agentMarketplace.listings.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchTerm"
            [placeholder]="t('growth.agentMarketplace.listings.search')" />
          <p-select
            [options]="lifecycleOptions()"
            [(ngModel)]="lifecycleFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.agentMarketplace.listings.filterLifecycle')" />
          <button pButton type="button" [label]="t('growth.agentMarketplace.listings.search')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('growth.agentMarketplace.listings.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.agentMarketplace.listings.actions.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          @if (canInstallSelected()) {
            <button
              pButton
              type="button"
              [label]="t('growth.agentMarketplace.listings.install')"
              [loading]="actionKey() === selected()?.key && actionType() === 'install'"
              (click)="installSelected()"></button>
          }
          <a class="link-btn" routerLink="/growth/marketplace/agent-installs">{{ t('growth.agentMarketplace.listings.openInstalls') }}</a>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="key">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.agentMarketplace.listings.columns.key') }}</th>
              <th>{{ t('growth.agentMarketplace.listings.columns.displayName') }}</th>
              <th>{{ t('growth.agentMarketplace.listings.columns.version') }}</th>
              <th>{{ t('growth.agentMarketplace.listings.columns.publisherType') }}</th>
              <th>{{ t('growth.agentMarketplace.listings.columns.installLifecycleStatus') }}</th>
              <th>{{ t('growth.agentMarketplace.listings.columns.killSwitchEnabled') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.key)">{{ row.key }}</button>
              </td>
              <td>{{ row.displayName }}</td>
              <td>{{ row.version }}</td>
              <td>{{ row.publisherType }}</td>
              <td>{{ lifecycleLabel(row.installLifecycleStatus) }}</td>
              <td>{{ row.killSwitchEnabled ? t('common.yes') : t('common.no') }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
  `]
})
export class GrowthAgentMarketplaceListingsComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthAgentListingResponse[]>([]);
  readonly filteredRows = signal<GrowthAgentListingResponse[]>([]);
  readonly selected = signal<GrowthAgentListingResponse | null>(null);
  readonly errorMessage = signal('');
  readonly actionKey = signal<string | null>(null);
  readonly actionType = signal<'install' | null>(null);

  searchTerm = '';
  lifecycleFilter: string | null = null;

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthMarketplaceChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  lifecycleOptions(): Array<{ label: string; value: string }> {
    return GROWTH_AGENT_INSTALL_LIFECYCLE_STATUSES.map(value => ({
      value,
      label: this.lifecycleLabel(value)
    }));
  }

  lifecycleLabel(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    return this.t(`growth.agentMarketplace.lifecycle.${value}`);
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listAgentMarketplaceListings(true).subscribe({
      next: data => {
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.agentMarketplace.listings.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    let filtered = this.rows();
    if (this.lifecycleFilter) {
      filtered = filtered.filter(item => item.installLifecycleStatus === this.lifecycleFilter);
    }
    if (term) {
      filtered = filtered.filter(item =>
        item.key.toLowerCase().includes(term)
        || item.displayName.toLowerCase().includes(term)
        || item.agentDefinitionKey.toLowerCase().includes(term));
    }
    this.filteredRows.set(filtered);
    const currentKey = this.selected()?.key;
    this.selected.set(filtered.find(item => item.key === currentKey) ?? filtered[0] ?? null);
  }

  onSelect(row: GrowthAgentListingResponse | null): void {
    this.selected.set(row);
  }

  canInstallSelected(): boolean {
    return !!this.selected()?.canInstall;
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.key);
  }

  openDetail(listingKey: string): void {
    void this.router.navigate(['/growth/marketplace/listings', listingKey]);
  }

  installSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.actionKey.set(row.key);
    this.actionType.set('install');
    this.errorMessage.set('');
    this.api.installAgentMarketplaceListing(row.key).pipe(finalize(() => {
      this.actionKey.set(null);
      this.actionType.set(null);
    })).subscribe({
      next: () => this.load(),
      error: () => this.errorMessage.set(this.t('growth.agentMarketplace.listings.messages.installFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthMarketplaceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
